import { useMemo } from "react";

interface BloodPressureData {
  date: string;
  systolic: number;
  diastolic: number;
}

interface HourlyBloodPressureData {
  datetime: string;
  hour: number;
  systolic: number;
  diastolic: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface UseBloodPressureCalculationsProps {
  filteredData: (BloodPressureData | HourlyBloodPressureData)[];
  timeRange: TimeRange;
  navigationOffset: number;
}

interface UseBloodPressureCalculationsReturn {
  timeContext: string;
  dateRange: string;
  breakdownTitle: string;
}

export const useBloodPressureCalculations = ({
  filteredData,
  timeRange,
  navigationOffset,
}: UseBloodPressureCalculationsProps): UseBloodPressureCalculationsReturn => {
  return useMemo(() => {
    // Function to get time context for each tab
    const getTimeContext = (
      data: (BloodPressureData | HourlyBloodPressureData)[],
      timeRange: TimeRange,
      navigationOffset: number,
    ) => {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const currentDate = new Date().getDate();

      // Apply navigation offset
      const baseDate = new Date(currentYear, currentMonth, currentDate);
      baseDate.setDate(baseDate.getDate() + navigationOffset);

      const offsetYear = baseDate.getFullYear();
      const offsetMonth = baseDate.getMonth();
      const offsetDate = baseDate.getDate();

      switch (timeRange) {
        case "1d": {
          const today = new Date(offsetYear, offsetMonth, offsetDate);
          return today.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        }

        case "7d": {
          const weekStart = new Date(
            offsetYear,
            offsetMonth,
            offsetDate - baseDate.getDay() + 1,
          );
          const weekEnd = new Date(
            offsetYear,
            offsetMonth,
            offsetDate - baseDate.getDay() + 7,
          );

          return `${weekStart.toLocaleDateString("en-US", { month: "long", day: "numeric" })} - ${weekEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`;
        }

        case "1m": {
          const monthStart = new Date(offsetYear, offsetMonth, 1);
          const monthEnd = new Date(offsetYear, offsetMonth + 1, 0);
          const startDate = monthStart.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
          });
          const endDate = monthEnd.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
          return `${startDate} - ${endDate}`;
        }

        case "6m": {
          const isFirstHalf = offsetMonth < 6;
          const periodStart = new Date(offsetYear, isFirstHalf ? 0 : 6, 1);
          const periodEnd = new Date(offsetYear, isFirstHalf ? 5 : 11, 30);

          return `${periodStart.toLocaleDateString("en-US", { month: "long" })} - ${periodEnd.toLocaleDateString("en-US", { month: "long", year: "numeric" })}`;
        }

        case "1y": {
          return "January - December " + offsetYear.toString();
        }

        default:
          return "";
      }
    };

    // Function to format date range
    const formatDateRange = (
      data: (BloodPressureData | HourlyBloodPressureData)[],
      _timeRange: TimeRange,
    ) => {
      if (data.length === 0) return "";

      if (data.length === 1) {
        const item = data[0];
        const dateStr =
          "date" in item ? item.date : item.datetime.split("T")[0];
        return formatShortDate(dateStr);
      }

      const firstItem = data[0];
      const lastItem = data[data.length - 1];

      const firstDateStr =
        "date" in firstItem ? firstItem.date : firstItem.datetime.split("T")[0];
      const lastDateStr =
        "date" in lastItem ? lastItem.date : lastItem.datetime.split("T")[0];

      const firstDate = formatShortDate(firstDateStr);
      const lastDate = formatShortDate(lastDateStr);
      return `${firstDate} – ${lastDate}`;
    };

    // Helper function for formatDateRange
    const formatShortDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    };

    // Function to get breakdown title
    const getBreakdownTitle = (timeRange: TimeRange) => {
      switch (timeRange) {
        case "1d":
          return "Hourly Breakdown";
        case "7d":
        case "1m":
          return "Daily Breakdown";
        case "6m":
        case "1y":
          return "Monthly Breakdown";
        default:
          return "Data Breakdown";
      }
    };

    return {
      timeContext: getTimeContext(filteredData, timeRange, navigationOffset),
      dateRange: formatDateRange(filteredData, timeRange),
      breakdownTitle: getBreakdownTitle(timeRange),
    };
  }, [filteredData, timeRange, navigationOffset]);
};
